import { BuildingsResponse } from 'apolloClient/types';
import { NeighborhoodsResponse } from 'apolloClient/types/Neighborhood';
import { HeaderData } from '../Navigation';

// headerData returns 50 buildings per page
const BUILDINGS_PAGE_SIZE = 50;

// ids are GraphQL IDs, so strings at runtime — typed as number like every
// other Strapi id in this app
export type HeaderNeighborhoodItem = {
  id: number;
  name: string | null;
  slug: string;
  cityName: string | null;
  citySlug: string | null;
  countyId: number | null;
  countyName: string | null;
};

export type HeaderBuildingItem = {
  id: number;
  name: string | null;
  slug: string;
  neighborhoodName: string | null;
  neighborhoodSlug: string | null;
};

export type HeaderNeighborhoodList = {
  total: number;
  items: HeaderNeighborhoodItem[];
};

export type HeaderBuildingList = {
  page: number;
  total: number;
  items: HeaderBuildingItem[];
};

export type HeaderDataRaw = {
  headerData: {
    neighborhoods: HeaderNeighborhoodList | null;
    buildings: HeaderBuildingList | null;
  };
};

// The menus were written against Strapi collection responses and read only a
// handful of their fields; these rebuild exactly those fields. The casts are
// the same partial-entity shortcut the old generic queries relied on — only
// the requested fields were ever present.
const toNeighborhoods = (list: HeaderNeighborhoodList): NeighborhoodsResponse =>
  ({
    data: list.items.map((item) => ({
      id: item.id,
      attributes: {
        name: item.name,
        slug: item.slug,
        // NeighborhoodMenuBody groups a neighbourhood without a city under
        // "Other", which it detects from an empty city
        city: {
          data: item.cityName
            ? {
                id: 0,
                attributes: {
                  name: item.cityName,
                  slug: item.citySlug,
                  county: {
                    data: item.countyName
                      ? {
                          id: item.countyId,
                          attributes: { name: item.countyName },
                        }
                      : null,
                  },
                },
              }
            : null,
        },
      },
    })),
    meta: {
      pagination: {
        page: 1,
        pageSize: list.items.length,
        pageCount: 1,
        total: list.total,
      },
    },
  } as unknown as NeighborhoodsResponse);

const toBuildings = (list: HeaderBuildingList): BuildingsResponse =>
  ({
    data: list.items.map((item) => ({
      id: item.id,
      attributes: {
        name: item.name,
        slug: item.slug,
        neighborhood: {
          data: item.neighborhoodSlug
            ? {
                id: 0,
                attributes: {
                  name: item.neighborhoodName,
                  slug: item.neighborhoodSlug,
                },
              }
            : null,
        },
      },
    })),
    meta: {
      pagination: {
        page: list.page,
        pageSize: BUILDINGS_PAGE_SIZE,
        pageCount: Math.ceil(list.total / BUILDINGS_PAGE_SIZE),
        total: list.total,
      },
    },
  } as unknown as BuildingsResponse);

export const toHeaderData = (raw?: HeaderDataRaw): HeaderData | undefined => {
  const header = raw?.headerData;

  if (!header) return undefined;

  return {
    neighborhoods: header.neighborhoods
      ? toNeighborhoods(header.neighborhoods)
      : undefined,
    buildings: header.buildings ? toBuildings(header.buildings) : undefined,
  } as HeaderData;
};
